import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';

interface OnlineStatusOptions {
  showNotifications?: boolean;
  checkInterval?: number;
  pingUrl?: string;
}

export const useOnlineStatus = (options: OnlineStatusOptions = {}) => {
  const {
    showNotifications = true,
    checkInterval = 30000, // 30 seconds
    pingUrl = '/api/v1/health/',
  } = options;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [offlineSnackbarId, setOfflineSnackbarId] = useState<string | number | undefined>();

  const checkServerConnection = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(pingUrl, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache',
      });

      clearTimeout(timeoutId);
      setIsServerReachable(response.ok);
      setLastChecked(new Date());
      return response.ok;
    } catch (error) {
      setIsServerReachable(false);
      setLastChecked(new Date());
      return false;
    }
  }, [pingUrl]);

  const handleOnline = useCallback(() => {
    setIsOnline(true);

    if (showNotifications && offlineSnackbarId) {
      closeSnackbar(offlineSnackbarId);
      setOfflineSnackbarId(undefined);
      enqueueSnackbar('Connection restored', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    }

    // Check server connection when coming back online
    checkServerConnection();
  }, [showNotifications, enqueueSnackbar, closeSnackbar, checkServerConnection, offlineSnackbarId]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setIsServerReachable(false);

    if (showNotifications) {
      const id = enqueueSnackbar('You are offline', {
        variant: 'warning',
        persist: true,
      }) as string | number;
      setOfflineSnackbarId(id);
    }
  }, [showNotifications, enqueueSnackbar]);

  useEffect(() => {
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (navigator.onLine) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async: state is set after await
      checkServerConnection();
    }

    // Set up periodic checks when online
    let intervalId: NodeJS.Timeout;
    if (checkInterval > 0) {
      intervalId = setInterval(() => {
        if (navigator.onLine) {
          checkServerConnection();
        }
      }, checkInterval);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (offlineSnackbarId) {
        closeSnackbar(offlineSnackbarId);
      }
    };
  }, [handleOnline, handleOffline, checkServerConnection, checkInterval, closeSnackbar]);

  return {
    isOnline,
    isServerReachable,
    isFullyConnected: isOnline && isServerReachable,
    lastChecked,
    checkConnection: checkServerConnection,
  };
};
